import React, { useLayoutEffect } from 'react';
import { Box, Container, Skeleton, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Header from '../Header';
import SiteFooter from '../SiteFooter';
import DotGridBackground from '../DotGridBackground';
import MultiModalLayout from '../MultiModalLayout';
import { useOptionalChatPanel } from '../../contexts/ChatPanelContext';
import ChatToggleButton from '../ChatToggleButton';
import PageHeader from './PageHeader';

/**
 * Container width per page kind (design.md §5.3).
 *  form      - sm   auth and single-column forms
 *  reading   - md   legal, blog posts, profile
 *  dashboard - lg   dashboards and lists
 *  wide      - xl   dense admin tables and multi-column workspaces
 *  tool      - none full-bleed; the page lays out its own columns
 */
const CONTAINER_WIDTH = {
  form: 'sm',
  reading: 'md',
  dashboard: 'lg',
  wide: 'xl',
  tool: false,
};

/** Default loading placeholder: a title block and two content cards. */
export const PageSkeleton = () => (
  <Stack spacing={2} aria-busy="true" aria-live="polite">
    <Skeleton variant="rounded" width="40%" height={40} />
    <Skeleton variant="text" width="60%" />
    <Skeleton variant="rounded" height={160} />
    <Skeleton variant="rounded" height={320} />
  </Stack>
);

/**
 * PageShell - the one page skeleton (design.md §5.3).
 *
 * Draws the theme gradient, the fixed Header, the content offset that clears
 * it, a Container sized by `kind`, an optional PageHeader, and SiteFooter on
 * public pages. Loading is rendered inside the shell so the Header never
 * disappears while data arrives.
 *
 * Props:
 *  - kind        : 'form' | 'reading' | 'dashboard' | 'wide' | 'tool' (default 'dashboard')
 *  - fixed       : lock the page to the viewport height; children scroll
 *                  themselves (search + map, chat, history tools)
 *  - siteHeader  : render the fixed site Header and the offset that clears it
 *                  (default true; public listing pages can drop it)
 *  - footer      : render SiteFooter (public pages)
 *  - chat        : dock the chat side panel beside the page (MultiModalLayout keeps the room;
 *                  the panel itself is MultiModalLayout.js::DockedChatPanel, drawn once in App.js).
 *                  Every other page gets a floating chat button instead, so the
 *                  assistant is reachable everywhere; `hideChat` opts out.
 *  - hideChat    : suppress the floating button (the chat page, auth, status pages)
 *  - dots        : true or DotGridBackground props - decorative dot grid
 *  - decoration  : extra absolutely positioned decoration (blobs, lines)
 *  - background  : 'gradient' (default) | 'none' | any CSS background value
 *  - loading     : show `skeleton` (or the default PageSkeleton) instead of children
 *  - skeleton    : node used while `loading`
 *  - title, lead, eyebrow, icon, titleAdornment, back, breadcrumbs, actions,
 *    headerSx    : forwarded to PageHeader; the header is omitted when there is no title
 *  - sx          : root styles; mainSx: the offset column; containerSx: the Container
 */
const PageShell = ({
  kind = 'dashboard',
  fixed = false,
  siteHeader = true,
  footer = false,
  chat = false,
  hideChat = false,
  dots = false,
  decoration = null,
  background = 'gradient',
  loading = false,
  skeleton,
  title,
  lead,
  eyebrow,
  icon,
  titleAdornment,
  back,
  breadcrumbs,
  actions,
  headerSx,
  sx,
  mainSx,
  containerSx,
  children,
}) => {
  const theme = useTheme();
  // The chat panel is drawn once above the routes; each page says whether it docks it, before
  // paint, so the panel never shows on a page without it. Nothing is said on unmount: the answer
  // holds while the next page loads.
  const setPageDocksChat = useOptionalChatPanel()?.setPageDocksChat;
  useLayoutEffect(() => {
    setPageDocksChat?.(chat);
  }, [chat, setPageDocksChat]);
  const maxWidth = CONTAINER_WIDTH[kind] ?? 'md';
  const isTool = kind === 'tool';

  const resolvedBackground =
    background === 'gradient'
      ? theme.modern.backgrounds.gradient
      : background === 'none'
        ? 'transparent'
        : background;

  const header = title ? (
    <PageHeader
      title={title}
      lead={lead}
      eyebrow={eyebrow}
      icon={icon}
      titleAdornment={titleAdornment}
      back={back}
      breadcrumbs={breadcrumbs}
      actions={actions}
      sx={headerSx}
    />
  ) : null;

  const body = loading ? (skeleton || <PageSkeleton />) : children;

  const content = isTool ? (
    <>
      {header}
      {loading ? (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 3, sm: 4 } }}>
          {body}
        </Container>
      ) : (
        body
      )}
    </>
  ) : (
    <Container
      maxWidth={maxWidth}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        py: { xs: 2, sm: 3 },
        px: { xs: 3, sm: 4 },
        ...(fixed && { minHeight: 0 }),
        ...containerSx,
      }}
    >
      {header}
      {body}
    </Container>
  );

  const shell = (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100dvh',
        background: resolvedBackground,
        // `clip` keeps decorations from widening the page without creating a
        // scroll container, so sticky children keep working.
        overflowX: 'clip',
        ...(fixed && { height: '100dvh', overflow: 'hidden' }),
        ...sx,
      }}
    >
      {dots && <DotGridBackground {...(typeof dots === 'object' ? dots : {})} />}
      {decoration}

      {siteHeader && <Header />}

      <Box
        component="main"
        sx={{
          position: 'relative',
          zIndex: 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          ...(siteHeader && { pt: { xs: 8, sm: 9 } }),
          ...(fixed ? { minHeight: 0, overflow: 'hidden' } : { pb: { xs: 4, sm: 6 } }),
          ...mainSx,
        }}
      >
        {content}
      </Box>

      {footer && <SiteFooter />}

      {/* Chat is reachable from every page: docked beside working pages, and a
          floating button everywhere else. `hideChat` is for the places where it
          would be noise — the chat page itself, auth, and status pages. */}
      {/* {!chat && !hideChat && <ChatToggleButton position="bottom-right" />} */}
    </Box>
  );

  return chat ? <MultiModalLayout>{shell}</MultiModalLayout> : shell;
};

export default PageShell;
